import { Context, createContext, FC, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react'
import { PaletteMode, useMediaQuery } from '@mui/material'
import { useLocalStorage } from '@dhl/use-storage'

export type PreferredColorScheme = 'dark' | 'light' | 'system'

export type ColorSchemeContextData = {
  activePaletteMode: PaletteMode
  preferredColorScheme: PreferredColorScheme
  setPreferredColorScheme: (mode: PreferredColorScheme) => void
  setPaletteMode: (mode: PaletteMode) => void
}

export const ColorSchemeContext: Context<ColorSchemeContextData> = createContext<ColorSchemeContextData>({
  activePaletteMode: 'light',
  preferredColorScheme: 'light',
  setPreferredColorScheme: () => {},
  setPaletteMode: () => {},
})

const toMuiPaletteMode = (scheme: PreferredColorScheme): PaletteMode => (scheme === 'dark' ? 'dark' : 'light')

const paletteModeFromBool = (isDark: boolean): PaletteMode => (isDark ? 'dark' : 'light')

type Props = PropsWithChildren<{
  /**
   * Check for user preferences and set the theme accordingly
   * unless the user has interacted with theme settings for DHL before
   *
   * @default false
   */
  detectTheme?: boolean
}>

export const ColorSchemeProvider: FC<Props> = ({ children, detectTheme = false }) => {
  /**
   * This is true when you set the preferences on browser level.
   * But also on system level, so macOs dark will also be the prefered in the media query!
   */
  const hasDarkModePreference = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true })

  /**
   * This part is a brainfuck
   * When nothing is stored yet we will store its value based on the users real preferrence `hasDarkModePreference`
   * But, when the user has interacted with DHL before the preferrences then this become what is currently already
   * active. So thats not a preferrence but the last interacted value...
   * TODO: We need to alter namings.
   */
  const [preferredColorScheme, storePreferredColorScheme] = useLocalStorage<PreferredColorScheme>(
    'preferred-color-scheme',
    // When we didnt store any value yet and no darkmode preference option wanted we set it to 'light'
    detectTheme && hasDarkModePreference ? 'dark' : 'light'
  )

  /**
   * Our initital active palette, which is something different then prefered palette
   */
  const initialPaletteMode =
    detectTheme && preferredColorScheme === 'system' && hasDarkModePreference
      ? 'dark'
      : toMuiPaletteMode(preferredColorScheme)

  const [activePaletteMode, setActivePaletteMode] = useState<PaletteMode>(initialPaletteMode)

  const setPreferredColorScheme = useCallback(
    (scheme: PreferredColorScheme) => {
      storePreferredColorScheme(scheme)

      let newPaletteMode = toMuiPaletteMode(scheme)

      if (scheme === 'system') {
        newPaletteMode = paletteModeFromBool(hasDarkModePreference)
      }

      setActivePaletteMode(newPaletteMode)
    },
    [hasDarkModePreference, storePreferredColorScheme]
  )

  // Update the active palette mode when the system appearance preference changes
  useEffect(() => {
    if (preferredColorScheme === 'system') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setActivePaletteMode(hasDarkModePreference ? 'dark' : 'light')
    }
  }, [hasDarkModePreference, preferredColorScheme, storePreferredColorScheme])

  const colorScheme: ColorSchemeContextData = useMemo(
    () => ({
      activePaletteMode,
      preferredColorScheme,
      setPreferredColorScheme,
      setPaletteMode: setActivePaletteMode,
    }),
    [activePaletteMode, preferredColorScheme, setPreferredColorScheme]
  )

  return <ColorSchemeContext.Provider value={colorScheme}>{children}</ColorSchemeContext.Provider>
}
